import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import {
	applyActionCode,
} from 'firebase/auth'
import { auth } from '@/constants'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiFetch, parseFirebaseErrorCode } from '@/utils'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card } from '@/components/ui/card'

const signupFormSchema = z
	.object({
		password: z
			.string()
			.min(8, 'Password must be at least 8 characters')
			.regex(
				/^(?=.*[!@#$%^&*-])(?=.*[0-9])(?=.*[A-Z]).{8,}$/,
				'Password must contain at least one uppercase letter, one number, and one special character'
			),
		confirmPassword: z.string().min(8, 'Password confirmation is required')
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ['confirmPassword']
	})

type IChangePassword = z.infer<typeof signupFormSchema>

export default function AuthActionsPage() {
	const [mode, setMode] = useState<string | null>(null)
	const [oobCode, setOobCode] = useState<string | null>(null)
	const [status, setStatus] = useState<
		'loading' | 'success' | 'error' | 'form'
	>('loading')
	const [error, setError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const [location, navigate] = useLocation()
	const form = useForm<IChangePassword>({
		resolver: zodResolver(signupFormSchema),
		defaultValues: {
			password: '',
			confirmPassword: ''
		}
	})

	// Redirect to home when error occurs for non-verifyEmail modes
	useEffect(() => {
		if(status === 'error' && mode !== 'verifyEmail') {
			navigate('/')
		}
	}, [status, mode, navigate])

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search)
		const modeParam = searchParams.get('mode')
		const oobCodeParam = searchParams.get('oobCode')

		setMode(modeParam)
		setOobCode(oobCodeParam)

		if(!modeParam || !oobCodeParam) {
			setStatus('error')
			setError('Invalid link. Please use the correct link from your email.')
			return
		}

		if(modeParam === 'verifyEmail') {
			const verifyEmail = async () => {
				try {
					await applyActionCode(auth, oobCodeParam)
					console.log({ currentUser: auth.currentUser })
					setStatus('success')
				} catch(error: any) {
					console.error('Email verification error:', error)
					const message = parseFirebaseErrorCode(error?.code)
					setStatus('error')
					setError(`Email verification failed. ${message}`)
				}
			}
			verifyEmail()
		}

		if(modeParam === 'resetPassword') {
			setStatus('form')
		}

		// if (modeParam === 'resetPassword') {
		//   const validateResetCode = async () => {
		//     try {
		//       await verifyPasswordResetCode(auth, oobCodeParam)
		//       setStatus('form') // show password reset form
		//     } catch (error: any) {
		//       console.error('Reset code invalid:', error)
		//       const message = parseFirebaseErrorCode(error?.code)
		//       setStatus('error')
		//       setError(`Password reset link is invalid or expired. ${message}`)
		//     }
		//   }
		//   validateResetCode()
		// }
	}, [location])

	const handlePasswordReset = async (data: IChangePassword) => {
		const { password, confirmPassword } = data

		if(password !== confirmPassword) {
			setError('Passwords do not match.')
			return
		}

		if(!oobCode) return

		try {
			setError(null)
			setIsSubmitting(true)
			//   await confirmPasswordReset(auth, oobCode, password)
			const response = await apiFetch('/v1/users/password-reset/confirm', {
				method: 'POST',
				body: JSON.stringify({
					oobCode,
					newPassword: password
				})
			})

			const result = await response.json()

			if(!result.success) {
				setError(`Password reset failed. Please, double check your reset link. (${result.error?.message ?? 'Unknown error'})`)
			} else {
				setStatus('success')
			}

		} catch(error: any) {
			setStatus('error')
			setError(`Password reset failed. Please double check your link.`)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className="container max-w-lg mx-auto p-4">
			{status === 'loading' && (
				<div className="flex flex-col items-center gap-4 animate-in fade-in-0">
					<Loader2 className="h-8 w-8 animate-spin text-primary"/>
					<h2 className="text-xl font-semibold">
						{mode === 'verifyEmail'
							? 'Verifying your email...'
							: 'Preparing password reset...'}
					</h2>
					<p className="text-muted-foreground text-sm">Please wait a moment.</p>
				</div>
			)}

			{status === 'form' && (
				<Card className="p-6">
					<div className="text-center mb-6">
						<h1 className="text-2xl font-bold">Change password</h1>
						<p className="text-sm text-muted-foreground mt-2">
							Here you can set your new password.
						</p>
					</div>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handlePasswordReset)}
							className="space-y-4">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<Input
												type="password"
												{...field}
												autoComplete="new-password"
												placeholder="minimum 8 symbols"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="confirmPassword"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Confirm Password</FormLabel>
										<FormControl>
											<Input
												type="password"
												{...field}
												autoComplete="new-password"
												placeholder="repeat password"
											/>
										</FormControl>
										<FormMessage/>
									</FormItem>
								)}
							/>

							{error && (
								<p className="text-sm text-primary">{error}</p>
							)}

							<Button type="submit" className="w-full" disabled={isSubmitting}>
								{isSubmitting ? 'Changing password...' : 'Change password'}
							</Button>
						</form>
					</Form>

					<div className="mt-4 text-center space-y-2">
						<p className="text-sm text-muted-foreground">
							Remember your password?{' '}
							<Button
								variant="link"
								className="p-0 h-auto font-semibold"
								onClick={() => navigate('/login')}>
								Log in
							</Button>
						</p>
						{/* <Button
              variant="link"
              className="text-sm text-muted-foreground"
              onClick={() => navigate('/')}>
              Back to Home
            </Button> */}
					</div>
				</Card>
			)}

			{status === 'success' && (
				<div className="flex flex-col items-center gap-4 animate-in fade-in-0">
					<CheckCircle className="h-12 w-12 text-green-500"/>
					<h2 className="text-2xl font-bold">
						{mode === 'verifyEmail'
							? 'Email Verified!'
							: 'Password Changed Successfully!'}
					</h2>
					<p className="text-muted-foreground">
						{mode === 'verifyEmail'
							? 'Your email has been successfully confirmed. You can now sign in.'
							: 'Your password has been changed. You can now sign in with your new password.'}
					</p>
					<Button asChild className="mt-4" onClick={() => navigate('/login')}>
						Go to Login
					</Button>
				</div>
			)}

			{status === 'error' && mode === 'verifyEmail' && (
				<div className="flex flex-col items-center gap-4 animate-in fade-in-0">
					<XCircle className="h-12 w-12 text-red-500"/>
					<h2 className="text-2xl font-bold">Verification Failed</h2>
					<p className="text-muted-foreground">{error}</p>
					<Button asChild variant="outline" className="mt-4">
						<a href="/">Return Home</a>
					</Button>
				</div>
			)}
		</div>
	)
}